import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetCompanyPeerGroup,
  GetInvestorPeerGroup,
  AddSelectionCompanyPeerGroup,
  GetPeerGroupsData,
  AddSelectionInvestorPeerGroup,
  ValidateOldPasswordHash,
  HandleChangePassword,
} from '../../utils/preferences-util';
import { TokenDecode } from '../../utils/general-util';
import prodConst from '../../constants/ProductConstants';
import { NUMBER_TWO, NUMBER_FOUR } from '../../constants/NumberConstants';
import { GetUserDashboard } from '../../utils/dashboard-util';
import { getPreferences } from './utils';
import { preferencesResponseSchema } from '../currency/currency.validation';
import { DEFAULT_CURRENCY_CODE } from '../../../shared/constants/currency-constants';
import { validateNewPassword } from '@shared/utils/password-policy';

export const getRawPreferences = createAsyncThunk('getRawPreferences', getPreferences);

export const getPreferencesdata = createAsyncThunk('getPreferencesdata', async () => {
  let responseCompanyPeerGroup = null;
  let responseInvestorPeerGroup = null;
  let responsePeerGroups = null;
  let responseGetPreferences = null;

  try {
    responseCompanyPeerGroup = await GetCompanyPeerGroup();
  } catch (error) {
    // Failed to load company peer group
  }
  try {
    responseInvestorPeerGroup = await GetInvestorPeerGroup();
  } catch (error) {
    // Failed to load investor peer group
  }
  try {
    responsePeerGroups = await GetPeerGroupsData();
  } catch (error) {
    // Failed to load peer groups
  }
  try {
    responseGetPreferences = await getPreferences();
  } catch (error) {
    // Failed to load preferences (including /api/v2/user/settings)
  }
  async function getDisabledStatus(productId) {
    const response = await TokenDecode()
      .then(
        (res) =>
          res &&
          res.MemberShip &&
          res.MemberShip !== undefined &&
          res.MemberShip.length > 0 &&
          res.MemberShip.every(
            (item) => !(item.product_id === productId && (item.status === NUMBER_TWO || item.status === NUMBER_FOUR)),
          ),
      )
      .catch(() => false);
    return response;
  }
  const toBoolean = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
      const lower = value.toLowerCase().trim();
      return lower === 'true' || lower === '1' || lower === 'yes';
    }
    return Boolean(value);
  };

  // Safely extract values with fallbacks, handling null/undefined responseGetPreferences
  const dailyNewsletterValue =
    responseGetPreferences?.DailyNewsletter ?? responseGetPreferences?.daily_newsletter ?? undefined;
  const aisWeeklyNewsletterValue =
    responseGetPreferences?.AISWeeklyNewsletter ?? responseGetPreferences?.ais_weekly_newsletter ?? undefined;

  const myJson = [
    {
      id: 'Email1MonthlyNewsLetter',
      name: 'Receive Weekly / Monthly Newsletter',
      checked: toBoolean(responseGetPreferences?.Email1MonthlyNewsletter),
      disabled: false,
    },
    {
      id: 'Email2EmailUpdates',
      name: 'Receive Email Updates',
      checked: toBoolean(responseGetPreferences?.Email2EmailUpdates),
      disabled: false,
    },
    {
      id: 'Email33rdParty',
      name: 'Receive 3rd Party Emails',
      checked: toBoolean(responseGetPreferences?.Email33rdParty),
      disabled: false,
    },
    {
      id: 'DailyNewsletter',
      name: 'Receive Activism Daily News Summary Emails',
      checked: toBoolean(dailyNewsletterValue),
      isOpen: toBoolean(dailyNewsletterValue),
      disabled: await getDisabledStatus(prodConst.ACTIVISM),
      children: [
        {
          id: 'NAEmailUpdates',
          name: 'Receive North America Daily News Summary Update',
          disabled: false,
          checked: toBoolean(responseGetPreferences?.NAEmailUpdates),
        },
        {
          id: 'AAEmailUpdates',
          name: 'Receive Asia/Australasia Daily News Summary Update',
          disabled: false,
          checked: toBoolean(responseGetPreferences?.AAEmailUpdates),
        },
      ],
    },
    {
      id: 'AISWeeklyNewsletter',
      name: 'Receive Activist Shorts Weekly News Summary',
      checked: toBoolean(aisWeeklyNewsletterValue),
    },
  ];

  return {
    reponseEmailArrayList: myJson,
    responseCompanyPeerGroup,
    responseInvestorPeerGroup,
    responsePeerGroups,
    responseGetPreferences,
  };
});

export const validateOldPasswordHashReq = createAsyncThunk('ValidateOldPasswordHash', async (req) => {
  const response = await ValidateOldPasswordHash(req);
  return response;
});

export const handleChangePasswordReq = createAsyncThunk(
  'HandleChangePassword',
  async (payload, { rejectWithValue }) => {
    if (payload?.isHighbondUser) {
      return null;
    }

    const oldPwd = typeof payload?.txtPasswordExisting === 'string' ? payload.txtPasswordExisting.trim() : '';
    if (!oldPwd) {
      return rejectWithValue({
        changePasswordError: true,
        text: 'Current password is required.',
        errors: ['Current password is required.'],
      });
    }

    const newPwd = typeof payload?.txtNewpasswordAgain === 'string' ? payload.txtNewpasswordAgain.trim() : '';
    const confirmPwd = typeof payload?.txtNewpassword === 'string' ? payload.txtNewpassword.trim() : newPwd;
    if (newPwd !== confirmPwd) {
      return rejectWithValue({
        changePasswordError: true,
        text: "Password and confirm password don't match.",
        errors: ["Password and confirm password don't match."],
      });
    }

    const policy = validateNewPassword(newPwd);
    if (!policy.valid) {
      return rejectWithValue({
        changePasswordError: true,
        text: policy.errors[0] || 'Password does not meet requirements.',
        errors: policy.errors,
      });
    }

    const response = await HandleChangePassword({
      password: newPwd,
      txt_oldpassword: oldPwd,
    });
    if (response && typeof response === 'object' && response.changePasswordError) {
      return rejectWithValue(response);
    }
    if (response === false) {
      return rejectWithValue({
        changePasswordError: true,
        text: 'Password change failed. Please try again.',
        errors: [],
      });
    }
    return response;
  },
);

export const getUserDashboardPreferencesReq = createAsyncThunk('getUserDashboardPreferencesReq', async () => {
  const response = await GetUserDashboard();
  return response;
});

const parsePreferencesResponse = (value) => {
  const parsed = preferencesResponseSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

const userPreferencesInitialCurrencyState = {
  CompensationCurrency: DEFAULT_CURRENCY_CODE,
  CompensationCurrencySymbol: '$',
  CompensationCurrencyDescription: 'US Dollar',
};

const PreferencesSlice = createSlice({
  name: 'preferences',
  initialState: {
    investorPeerGroupDataList: [],
    investorPeerGroupSelection: undefined,
    companyPeerGroupDataList: [],
    companyPeerGroupSelection: undefined,
    validateOldPasswod: false,
    activimEmailPreferencesStatus: false,
    emailListItems: [],
    preferencesIsLoading: true,
    userPreferences: userPreferencesInitialCurrencyState,
    lstWidgetSelections: [],
    ddlWidgetSelections: [],
    cmpWidgetValue: undefined,
    invWidgetValue: undefined,
  },
  reducers: {
    handleCompanyPeerGroupSelectionChange: {
      reducer(state, action) {
        state.companyPeerGroupSelection = action.payload.companyPeerGroupSelection;
        if (action.payload.companyPeerGroupSelection !== undefined) {
          AddSelectionCompanyPeerGroup(
            action.payload.companyPeerGroupSelection !== null ? action.payload.companyPeerGroupSelection.value : null,
          );
        }
      },
      prepare(companyPeerGroupSelection) {
        return {
          payload: { companyPeerGroupSelection },
        };
      },
    },
    handleInvestorPeerGroupSelectionChange: {
      reducer(state, action) {
        state.investorPeerGroupSelection = action.payload.investorPeerGroupSelection;
        if (action.payload.investorPeerGroupSelection !== undefined) {
          AddSelectionInvestorPeerGroup(
            action.payload.investorPeerGroupSelection !== null ? action.payload.investorPeerGroupSelection.value : null,
          );
        }
      },
      prepare(investorPeerGroupSelection) {
        return {
          payload: { investorPeerGroupSelection },
        };
      },
    },
    handleEmailPreferences: {
      reducer(state, action) {
        state.emailListItems = action.payload.array;
      },
      prepare(array) {
        return {
          payload: { array },
        };
      },
    },
    handleResetLoading: {
      reducer(state) {
        state.preferencesIsLoading = true;
      },
      prepare() {
        return {
          payload: {},
        };
      },
    },
    handleCompanyWidgetSelection: {
      reducer(state, action) {
        const data = action.payload;
        let options = [];
        if (data.length > 0) {
          data.forEach((item) => {
            if (item.value !== null) {
              options = [...options, item];
              state.cmpWidgetValue = options.length > 0 ? options : undefined;
            } else {
              state.cmpWidgetValue = [{ label: 'All', value: null }];
            }
          });
        } else {
          state.cmpWidgetValue = [];
        }
      },
      prepare(e) {
        return { payload: e };
      },
    },
    handledInvestorWidgetSelection: {
      reducer(state, action) {
        const data = action.payload;
        let options = [];
        if (data.length > 0) {
          data.forEach((item) => {
            if (item.value !== null) {
              options = [...options, item];
              state.invWidgetValue = options;
            } else {
              state.invWidgetValue = [{ label: 'All', value: null }];
            }
          });
        } else {
          state.invWidgetValue = [];
        }
      },
      prepare(e) {
        return { payload: e };
      },
    },
    handleResetCompanyWidgetSelection: {
      reducer(state, action) {
        state.cmpWidgetValue = undefined;
      },
      prepare(e) {
        return {
          payload: e,
        };
      },
    },
    handleResetInvestorWidgetSelection: {
      reducer(state, action) {
        state.invWidgetValue = undefined;
      },
      prepare(e) {
        return {
          payload: e,
        };
      },
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPreferencesdata.fulfilled, (state, action) => {
        state.preferencesIsLoading = false;
        state.companyPeerGroupDataList = action.payload.responseCompanyPeerGroup?.recordset || [];
        state.investorPeerGroupDataList = action.payload.responseInvestorPeerGroup?.recordset || [];
        state.investorPeerGroupSelection = action.payload.responsePeerGroups?.investor_peer_group || [];
        state.companyPeerGroupSelection = action.payload.responsePeerGroups?.company_peer_group || [];
        state.emailListItems = action.payload.reponseEmailArrayList || [];
        state.userPreferences = parsePreferencesResponse(action.payload?.responseGetPreferences);
        if (!state.userPreferences?.CompensationCurrency) {
          state.userPreferences = {
            ...state.userPreferences,
            ...userPreferencesInitialCurrencyState,
          };
        }
      })
      .addCase(getPreferencesdata.rejected, (state) => {
        state.preferencesIsLoading = false;
        state.userPreferences = userPreferencesInitialCurrencyState;
        if (!state.companyPeerGroupDataList) {
          state.companyPeerGroupDataList = [];
        }
        if (!state.investorPeerGroupDataList) {
          state.investorPeerGroupDataList = [];
        }
      })
      .addCase(getPreferencesdata.pending, (state) => {
        state.preferencesIsLoading = true;
      })
      .addCase(getRawPreferences.fulfilled, (state, action) => {
        if (action.payload) {
          state.userPreferences = parsePreferencesResponse(action.payload);
          if (!state.userPreferences?.CompensationCurrency) {
            state.userPreferences = {
              ...state.userPreferences,
              ...userPreferencesInitialCurrencyState,
            };
          }
        } else {
          state.preferencesIsLoading = false;
          state.userPreferences = userPreferencesInitialCurrencyState;
        }
      })
      .addCase(getRawPreferences.rejected, (state) => {})
      .addCase(validateOldPasswordHashReq.fulfilled, (state, action) => {
        state.validateOldPasswod = action.payload;
      })
      .addCase(handleChangePasswordReq.fulfilled, () => {})
      .addCase(getUserDashboardPreferencesReq.fulfilled, (state, action) => {
        if (action.payload) {
          state.lstWidgetSelections = [];
          state.lstWidgetSelections = action.payload !== undefined && action.payload.length > 0 ? action.payload : [];
          if (action.payload.length > 0 && action.payload !== undefined) {
            state.ddlWidgetSelections = [{ label: 'All', value: null }];
            const data = action.payload;
            data.forEach((item) => {
              state.ddlWidgetSelections.push({
                label: item.widget_name,
                value: item.widget_id,
              });
            });
          } else {
            state.cmpWidgetValue = undefined;
            state.invWidgetValue = undefined;
          }
          state.dashboardSelection = [];
          let data = action.payload;
          if (typeof action.payload === 'string') {
            data = JSON.parse(action.payload);
          } else {
            data = action.payload;
          }
          data.forEach((e) => {
            state.dashboardSelectionId = e.dashboard_id;
            state.txtDashboardName = e.Dashboard_name;
            state.dashboardSelection.push({
              ...JSON.parse(e.position),
              dashboard_widget_link_id: e.dashboard_widget_link_id,
              company_search_id: e.company_search_id,
              investor_search_id: e.investor_search_id,
            });
          });
        }
      });
  },
});

export const {
  handleInvestorPeerGroupSelectionChange,
  handleResetLoading,
  handleCompanyPeerGroupSelectionChange,
  handleEmailPreferences,
  handleCompanyWidgetSelection,
  handledInvestorWidgetSelection,
  handleResetCompanyWidgetSelection,
  handleResetInvestorWidgetSelection,
} = PreferencesSlice.actions;

export default PreferencesSlice.reducer;
